import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ArrowRight, BookOpen, EyeOff, Lock } from "lucide-react";
import Header from "../components/Header";
import { fetchRoutines, deleteRoutine } from "../lib/api";
import { useStealth } from "../context/StealthContext";
import {
  stealthListRoutines,
  stealthDeleteRoutine,
} from "../lib/stealthDb";
import { toast } from "sonner";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const MyRoutines = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stealth } = useStealth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (stealth) {
        const local = await stealthListRoutines();
        // Map full RoutineResponse to the list-item shape the UI expects.
        // Stealth routines are local-only — never locked, since they
        // never leave the device and aren't subject to the server-side
        // library paywall.
        setItems(
          local.map((r) => ({
            id: r.id,
            intro: r.intro,
            goal: r.goal,
            experience: r.experience,
            beard_shadow: r.beard_shadow,
            created_at: r.created_at,
            step_count: r.steps?.length ?? 0,
            locked: false,
          }))
        );
      } else {
        const data = await fetchRoutines();
        setItems(data);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[my-routines] load failed:", e?.message || e);
      }
      toast.error(t("myRoutines.toastLoadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stealth]);

  const onDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("myRoutines.confirmDelete"))) return;
    try {
      if (stealth) {
        await stealthDeleteRoutine(id);
      } else {
        await deleteRoutine(id);
      }
      setItems((p) => p.filter((r) => r.id !== id));
      toast.success(t("myRoutines.toastRemoved"));
    } catch {
      toast.error(t("myRoutines.toastRemoveFailed"));
    }
  };

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />

      <section className="pt-36 pb-24 px-6" data-testid="my-routines-root">
        <div className="max-w-5xl mx-auto">
          {stealth && (
            <div
              className="mb-6 inline-flex items-center gap-2 bg-[#2C2123] text-[#FAF6F2] rounded-full px-4 py-1.5 text-xs tracking-wide"
              data-testid="stealth-mode-banner"
            >
              <EyeOff className="h-3.5 w-3.5" />
              {t("myRoutines.stealthBanner")}
            </div>
          )}
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
                {t("myRoutines.kicker")}
              </span>
              <h1 className="text-4xl sm:text-5xl font-light text-[#2C2123] leading-tight">
                {t("myRoutines.title")}
              </h1>
              <p className="text-[#5C4B51] mt-2 max-w-lg">
                {t("myRoutines.subline")}
              </p>
            </div>
            <Link
              to="/onboarding"
              className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
              data-testid="my-routines-new-btn"
            >
              <Plus className="h-4 w-4" />
              {t("myRoutines.newSession")}
            </Link>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-white border border-[#EADDD7] rounded-2xl p-6 h-40 animate-pulse"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl bg-white border border-[#EADDD7] p-14 text-center soft-shadow"
              data-testid="my-routines-empty"
            >
              <div className="h-16 w-16 rounded-full bg-[#F2E8E9] flex items-center justify-center mx-auto mb-5">
                <BookOpen className="h-7 w-7 text-[#9B6A6C]" />
              </div>
              <h3 className="text-2xl font-light text-[#2C2123] mb-2">
                {t("myRoutines.emptyTitle")}
              </h3>
              <p className="text-[#5C4B51] mb-6 max-w-md mx-auto">
                {t("myRoutines.emptyBody")}
              </p>
              <Link
                to="/onboarding"
                className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)]"
                data-testid="my-routines-empty-start-btn"
              >
                {t("myRoutines.emptyCta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  // Card is interactive but contains a nested delete
                  // button, so it can't be a real <button> (invalid HTML →
                  // hydration warning). Use div + role + key handler so
                  // a11y stays intact AND the delete button remains
                  // properly nested. Keyboard: Enter / Space open the
                  // routine; the inner trash button still receives its
                  // own focus + Enter handling for free.
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (r.locked) {
                      navigate("/pricing", {
                        state: { reason: "library_locked", routineId: r.id },
                      });
                      return;
                    }
                    navigate(`/routine/${r.id}`);
                  }}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return; // ignore nested button keys
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (r.locked) {
                        navigate("/pricing", {
                          state: { reason: "library_locked", routineId: r.id },
                        });
                      } else {
                        navigate(`/routine/${r.id}`);
                      }
                    }
                  }}
                  className={`group text-left bg-white border rounded-2xl p-6 transition-all soft-shadow cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B6A6C] focus-visible:ring-offset-2 relative ${
                    r.locked
                      ? "border-[#EADDD7] opacity-70 hover:opacity-90 hover:border-[#D5A8A4]"
                      : "border-[#EADDD7] hover:border-[#D5A8A4] hover:-translate-y-0.5"
                  }`}
                  data-testid={`my-routine-card-${r.id}`}
                >
                  {/* Lock badge for free-tier non-most-recent routines */}
                  {r.locked && (
                    <div className="absolute top-4 right-4 z-10">
                      <div className="h-8 w-8 rounded-full bg-[#9B6A6C] flex items-center justify-center shadow-md">
                        <Lock className="h-3.5 w-3.5 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-[#8A7D82]">
                      {formatDate(r.created_at)}
                    </span>
                    {!r.locked && (
                      <button
                        onClick={(e) => onDelete(r.id, e)}
                        className="h-8 w-8 rounded-full text-[#8A7D82] hover:bg-[#F8EEEE] hover:text-[#9B6A6C] transition-all flex items-center justify-center"
                        aria-label={t("myRoutines.deleteAria")}
                        data-testid={`my-routine-delete-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <p className="font-serif italic text-lg text-[#2C2123] leading-snug line-clamp-3 mb-4">
                    "{r.intro}"
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs bg-[#F2E8E9] text-[#5D4050] rounded-full px-3 py-1">
                      {t(`myRoutines.goalLabels.${r.goal}`, { defaultValue: r.goal })}
                    </span>
                    <span className="text-xs bg-[#F5EFEB] text-[#5C4B51] rounded-full px-3 py-1">
                      {t("myRoutines.stepsLabel", { count: r.step_count })}
                    </span>
                    {r.beard_shadow && (
                      <span className="text-xs bg-[#F8EEEE] text-[#5D4050] rounded-full px-3 py-1">
                        {t("myRoutines.colorCorrection")}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 inline-flex items-center text-sm text-[#9B6A6C] group-hover:gap-2 gap-1 transition-all">
                    {r.locked ? "Unlock with Pro" : t("myRoutines.openRoutine")}
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MyRoutines;
